<template>
  <div>
    <NavBar />
    <main class="page previsions-page">
      <header class="previsions-header">
        <div>
          <p class="eyebrow">Aide à la préparation</p>
          <h1 class="page-title">Prévisions</h1>
          <p class="page-subtitle">
            Prochains marchés prévus au planning, avec une tendance de chiffre d'affaires issue de l'historique.
          </p>
        </div>
      </header>

      <section class="notice-card">
        <strong>Prévision indicative</strong>
        <span>Prévision indicative basée sur l'historique. Ce n'est pas une garantie de chiffre d'affaires.</span>
      </section>

      <section class="period-filter" aria-label="Filtrer les prévisions par période">
        <div class="period-filter-top">
          <div>
            <span class="filter-label">Période</span>
            <strong>{{ periodeLisible }}</strong>
          </div>
        </div>

        <div class="period-actions" role="group" aria-label="Périodes rapides">
          <button
            v-for="option in optionsPeriode"
            :key="option.value"
            class="period-btn"
            :class="{ active: periodeType === option.value }"
            type="button"
            @click="selectionnerPeriode(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <div v-if="periodeType === 'custom'" class="custom-period">
          <label>
            <span>Du</span>
            <input v-model="dateDebut" type="date" @change="chargerPrevisions" />
          </label>
          <label>
            <span>Au</span>
            <input v-model="dateFin" type="date" @change="chargerPrevisions" />
          </label>
        </div>
      </section>

      <section v-if="afficherPointsAttention" class="attention-summary" aria-label="Points d'attention de la période">
        <div class="attention-summary-top">
          <h2>Points d’attention</h2>
        </div>

        <ul v-if="pointsAttention.length" class="attention-list">
          <li v-for="point in pointsAttention" :key="point.key">
            <strong>{{ point.total }}</strong>
            <span>{{ point.label }}</span>
          </li>
        </ul>
        <p v-else>Aucun point particulier sur cette période.</p>
      </section>

      <section v-if="loading" class="card state-card">
        <div class="loading-pulse"></div>
        <strong>Chargement des prévisions</strong>
        <span>Lecture des prochains marchés du planning...</span>
      </section>

      <section v-else-if="erreur" class="card state-card error-state">
        <strong>Impossible de charger les prévisions</strong>
        <span>{{ erreur }}</span>
        <button class="btn btn-secondary retry-btn" type="button" @click="chargerPrevisions">
          Réessayer
        </button>
      </section>

      <section v-else-if="previsions.length === 0" class="card state-card">
        <strong>Aucun prochain marché</strong>
        <span>Ajoutez vos prochains marchés au planning pour afficher une prévision indicative.</span>
        <router-link class="btn btn-primary state-link" to="/planning">
          Ouvrir le planning
        </router-link>
      </section>

      <section v-else-if="aucuneDonneeHistorique" class="card state-card">
        <strong>Aucune donnée historique exploitable</strong>
        <span>
          Des marchés sont bien planifiés, mais l'historique de chiffre d'affaires est insuffisant pour donner une fourchette.
        </span>
        <div class="mini-list">
          <div v-for="marche in previsions" :key="cleMarche(marche)" class="mini-market">
            <div class="mini-market-main">
              <span>{{ marche.ville || 'Ville non renseignée' }}</span>
              <small>{{ formatDate(marche.date) }} · {{ formatHoraires(marche) }}</small>
            </div>
            <span v-if="estVacancesScolaires(marche)" class="badge vacances-badge">
              Vacances scolaires : {{ nomVacancesScolaires(marche) }}
            </span>
            <span v-else-if="vacancesIndisponibles(marche)" class="vacances-unavailable">
              Info vacances indisponible
            </span>
          </div>
        </div>
      </section>

      <section v-else class="previsions-grid" aria-label="Prévisions des prochains marchés">
        <article
          v-for="(marche, index) in previsions"
          :key="cleCarte(marche, index)"
          class="prevision-card"
          :class="'potentiel-' + potentielClass(marche.potentiel)"
        >
          <div class="card-top">
            <div>
              <p class="date-line">{{ formatDate(marche.date) }}</p>
              <h2>{{ marche.ville || 'Ville non renseignée' }}</h2>
              <p class="hours-line">{{ formatHoraires(marche) }}</p>
            </div>
            <span class="badge badge-indicative">prévision indicative</span>
          </div>

          <div class="badges-row">
            <span class="badge potentiel-badge" :class="'badge-' + potentielClass(marche.potentiel)">
              Potentiel {{ marche.potentiel || 'inconnu' }}
            </span>
            <span class="badge confiance-badge" :class="'confiance-' + confianceClass(marche.confiance)">
              Confiance {{ marche.confiance || 'faible' }}
            </span>
            <span v-if="estVacancesScolaires(marche)" class="badge vacances-badge">
              Vacances scolaires : {{ nomVacancesScolaires(marche) }}
            </span>
            <span v-else-if="vacancesIndisponibles(marche)" class="vacances-unavailable">
              Info vacances indisponible
            </span>
          </div>

          <div v-if="afficherBlocMeteoCarte(marche)" class="weather-block">
            <div class="weather-header">
              <div class="weather-main">
                <span>Météo prévue</span>
                <strong>{{ titreMeteo(marche) }}</strong>
              </div>
              <span
                v-if="afficherBadgeImpactMeteo(marche)"
                class="weather-impact"
                :class="'impact-' + impactMeteoClass(marche)"
              >
                {{ libelleImpactMeteo(marche) }}
              </span>
            </div>
            <p v-if="afficherResumeMeteo(marche)" class="weather-summary">{{ resumeMeteo(marche) }}</p>
            <div v-if="donneesMeteoUtiles(marche).length" class="weather-details">
              <span v-for="donnee in donneesMeteoUtiles(marche)" :key="donnee">{{ donnee }}</span>
            </div>
          </div>

          <div class="merchant-advice">
            <span>Conseil marchand</span>
            <strong>{{ conseilMarchand(marche).action }}</strong>
            <small v-if="conseilMarchand(marche).precision">{{ conseilMarchand(marche).precision }}</small>
            <small v-if="conseilMeteo(marche)">{{ conseilMeteo(marche) }}</small>
            <small v-if="estVacancesScolaires(marche)">
              Surveiller l'impact des vacances sur la fréquentation.
            </small>
          </div>

          <div class="ca-block" :class="{ muted: !aFourchette(marche) }">
            <span class="ca-label">CA estimé</span>
            <strong v-if="aFourchette(marche)">
              {{ formatEuro(marche.ca_estime_bas) }} à {{ formatEuro(marche.ca_estime_haut) }}
            </strong>
            <strong v-else>Pas assez d'historique</strong>
          </div>

          <div v-if="aTendance(marche)" class="trend-block">
            <div class="trend-main">
              <span>Tendance récente</span>
              <strong class="badge trend-badge" :class="'trend-' + tendanceClass(marche.tendance.statut)">
                {{ libelleTendance(marche.tendance) }}
              </strong>
            </div>
            <small>{{ contexteTendance(marche.tendance) }}</small>
            <div v-if="afficherMontantsTendance(marche.tendance)" class="trend-values">
              <span>CA récent : {{ formatEuro(marche.tendance.ca_moyen_recent) }}</span>
              <span>Référence : {{ formatEuro(marche.tendance.ca_moyen_reference) }}</span>
            </div>
          </div>

          <div v-if="aHistorique(marche)" class="history-metrics">
            <div v-if="montantDisponible(marche.ca_moyen_historique)" class="history-row">
              <span>CA moyen historique</span>
              <strong>{{ formatEuro(marche.ca_moyen_historique) }}</strong>
            </div>
            <div v-if="montantDisponible(marche.ca_median_historique)" class="history-row">
              <span>Médiane prudente</span>
              <strong>{{ formatEuro(marche.ca_median_historique) }}</strong>
            </div>
          </div>

          <div class="detail-grid visible-details">
            <div class="detail-item">
              <span>Journées comparables</span>
              <strong>{{ marche.nb_journees_comparables || 0 }}</strong>
            </div>
          </div>

          <button
            class="details-toggle"
            type="button"
            :aria-expanded="detailsOuverts[cleCarte(marche, index)] ? 'true' : 'false'"
            @click="basculerDetails(marche, index)"
          >
            <span>{{ detailsOuverts[cleCarte(marche, index)] ? 'Masquer les détails' : 'Voir les détails' }}</span>
            <span class="toggle-arrow" aria-hidden="true">
              {{ detailsOuverts[cleCarte(marche, index)] ? '↑' : '↓' }}
            </span>
          </button>

          <transition name="details">
            <div v-if="detailsOuverts[cleCarte(marche, index)]" class="details-panel">
              <div class="detail-item long-detail">
                <span>Lecture simple</span>
                <strong>{{ phraseLecture(marche) }}</strong>
              </div>

              <div v-if="facteursVisibles(marche).length" class="factors">
                <h3>Facteurs explicatifs</h3>
                <ul>
                  <li v-for="facteur in facteursVisibles(marche)" :key="facteur">
                    {{ facteur }}
                  </li>
                </ul>
              </div>

              <div v-if="meteoIndisponible(marche)" class="detail-item long-detail">
                <span>Météo prévue</span>
                <strong>Météo indisponible</strong>
              </div>
            </div>
          </transition>
        </article>
      </section>
    </main>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api from '../api'

export default {
  name: 'Previsions',
  components: { NavBar },
  data() {
    return {
      loading: true,
      erreur: '',
      previsions: [],
      periodeType: '7',
      dateDebut: this.formatDateParam(new Date()),
      dateFin: this.dateDansJours(6),
      detailsOuverts: {},
      optionsPeriode: [
        { value: 'today', label: "Aujourd'hui", jours: 1 },
        { value: '7', label: '7 jours', jours: 7 },
        { value: '14', label: '14 jours', jours: 14 },
        { value: '30', label: '30 jours', jours: 30 },
        { value: 'custom', label: 'Personnalisé' }
      ]
    }
  },
  computed: {
    aucuneDonneeHistorique() {
      return this.previsions.length > 0 && this.previsions.every(marche => !this.aFourchette(marche))
    },
    afficherPointsAttention() {
      return !this.loading && !this.erreur && this.previsions.length > 0
    },
    pointsAttention() {
      const points = [
        {
          key: 'feries',
          total: this.previsions.filter(marche => this.estJourFerie(marche)).length,
          label: 'jour(s) férié(s)'
        },
        {
          key: 'vacances',
          total: this.previsions.filter(marche => this.estVacancesScolaires(marche)).length,
          label: 'marché(s) pendant les vacances'
        },
        {
          key: 'baisse',
          total: this.previsions.filter(marche => this.tendanceClass(marche?.tendance?.statut) === 'baisse').length,
          label: 'marché(s) en baisse récente'
        },
        {
          key: 'hausse',
          total: this.previsions.filter(marche => {
            const tendance = this.tendanceClass(marche?.tendance?.statut)
            return tendance === 'hausse' || tendance === 'forte-hausse'
          }).length,
          label: 'marché(s) en hausse récente'
        },
        {
          key: 'potentiel-faible',
          total: this.previsions.filter(marche => this.potentielClass(marche.potentiel) === 'faible').length,
          label: 'marché(s) à potentiel faible'
        },
        {
          key: 'potentiel-fort',
          total: this.previsions.filter(marche => {
            const potentiel = this.potentielClass(marche.potentiel)
            return potentiel === 'bon' || potentiel === 'tres-bon'
          }).length,
          label: 'marché(s) à potentiel fort'
        }
      ]

      return points.filter(point => point.total > 0)
    },
    periodeLisible() {
      if (this.dateDebut && this.dateFin && this.dateDebut === this.dateFin) {
        return this.formatDateCourte(this.dateDebut)
      }

      if (this.dateDebut && this.dateFin) {
        return `${this.formatDateCourte(this.dateDebut)} - ${this.formatDateCourte(this.dateFin)}`
      }

      if (this.dateDebut) {
        return `À partir du ${this.formatDateCourte(this.dateDebut)}`
      }

      if (this.dateFin) {
        return `Jusqu'au ${this.formatDateCourte(this.dateFin)}`
      }

      return 'Prochains marchés'
    }
  },
  mounted() {
    this.chargerPrevisions()
  },
  methods: {
    formatDateParam(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    dateDansJours(nombreJours) {
      const date = new Date()
      date.setDate(date.getDate() + nombreJours)
      return this.formatDateParam(date)
    },
    selectionnerPeriode(value) {
      this.periodeType = value

      if (value !== 'custom') {
        const option = this.optionsPeriode.find(item => item.value === value)
        const jours = option?.jours || 1
        this.dateDebut = this.formatDateParam(new Date())
        this.dateFin = this.dateDansJours(jours - 1)
      } else if (!this.dateDebut || !this.dateFin) {
        this.dateDebut = this.formatDateParam(new Date())
        this.dateFin = this.dateDebut
      }

      this.chargerPrevisions()
    },
    paramsPrevisions() {
      const params = {}

      if (this.dateDebut) params.date_debut = this.dateDebut
      if (this.dateFin) params.date_fin = this.dateFin

      return params
    },
    async chargerPrevisions() {
      this.loading = true
      this.erreur = ''
      try {
        const res = await api.get('/previsions/prochains-marches', {
          params: this.paramsPrevisions()
        })
        this.previsions = Array.isArray(res.data) ? res.data : []
      } catch (err) {
        this.erreur = err.response?.data?.error || "Vérifiez la connexion puis réessayez."
      } finally {
        this.loading = false
      }
    },
    cleMarche(marche) {
      return `${marche.date || ''}-${marche.ville || ''}-${marche.heure_debut || ''}`
    },
    cleCarte(marche, index) {
      return `${this.cleMarche(marche)}-${index}`
    },
    basculerDetails(marche, index) {
      const cle = this.cleCarte(marche, index)
      this.detailsOuverts[cle] = !this.detailsOuverts[cle]
    },
    aFourchette(marche) {
      return marche.ca_estime_bas !== null && marche.ca_estime_bas !== undefined &&
        marche.ca_estime_haut !== null && marche.ca_estime_haut !== undefined
    },
    montantDisponible(value) {
      return value !== null && value !== undefined && Number.isFinite(Number(value))
    },
    aHistorique(marche) {
      return this.montantDisponible(marche.ca_moyen_historique) ||
        this.montantDisponible(marche.ca_median_historique)
    },
    aTendance(marche) {
      return marche?.tendance && typeof marche.tendance === 'object' && Boolean(marche.tendance.statut)
    },
    formatPourcentage(value) {
      const pourcentage = Number(value)
      if (!Number.isFinite(pourcentage)) return ''

      return new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: 0
      }).format(Math.abs(pourcentage))
    },
    formatDateCourte(dateStr) {
      if (!dateStr) return ''
      const date = new Date(`${dateStr}T12:00:00`)
      return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'short'
      }).format(date)
    },
    libelleTendance(tendance) {
      const statut = String(tendance?.statut || '').toLowerCase()
      const pourcentage = this.formatPourcentage(tendance?.pourcentage)
      const suffixe = pourcentage ? ` de ${pourcentage} %` : ''

      if (statut === 'insuffisant') return 'Tendance insuffisante'
      if (statut === 'baisse') return `En baisse${suffixe}`
      if (statut === 'stable') return 'Stable'
      if (statut === 'hausse') return `En hausse${suffixe}`
      if (statut === 'forte_hausse') return `Forte hausse${suffixe}`
      return 'Tendance à confirmer'
    },
    tendanceClass(statut) {
      const value = String(statut || '').toLowerCase()
      if (value === 'baisse') return 'baisse'
      if (value === 'stable') return 'stable'
      if (value === 'hausse') return 'hausse'
      if (value === 'forte_hausse') return 'forte-hausse'
      return 'insuffisant'
    },
    contexteTendance(tendance) {
      const nbJournees = Number(tendance?.nb_journees_recentes)
      const total = Number.isFinite(nbJournees) && nbJournees > 0 ? nbJournees : 4
      return `Sur les ${total} derniers marchés comparables`
    },
    afficherMontantsTendance(tendance) {
      return this.montantDisponible(tendance?.ca_moyen_recent) &&
        this.montantDisponible(tendance?.ca_moyen_reference)
    },
    formatEuro(value) {
      const montant = Number(value || 0)
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
      }).format(montant)
    },
    formatDate(dateStr) {
      if (!dateStr) return 'Date non renseignée'
      const date = new Date(`${dateStr}T12:00:00`)
      return new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      }).format(date)
    },
    formatHeure(value) {
      return value ? String(value).slice(0, 5) : '--:--'
    },
    formatHoraires(marche) {
      return `${this.formatHeure(marche.heure_debut)} - ${this.formatHeure(marche.heure_fin)}`
    },
    contexteCalendrier(marche) {
      return marche?.contexte_calendrier || {}
    },
    estJourFerie(marche) {
      return this.contexteCalendrier(marche).jour_ferie === true
    },
    contexteVacances(marche) {
      return marche?.contexte_vacances || {}
    },
    estVacancesScolaires(marche) {
      return this.contexteVacances(marche).en_vacances === true
    },
    vacancesIndisponibles(marche) {
      return this.contexteVacances(marche).disponible === false
    },
    nomVacancesScolaires(marche) {
      return this.contexteVacances(marche).nom || 'période en cours'
    },
    contexteMeteo(marche) {
      return marche?.contexte_meteo || {}
    },
    aContexteMeteo(marche) {
      return Boolean(marche?.contexte_meteo && typeof marche.contexte_meteo === 'object')
    },
    meteoDisponible(marche) {
      return this.contexteMeteo(marche).disponible === true
    },
    meteoIndisponible(marche) {
      return this.aContexteMeteo(marche) && this.contexteMeteo(marche).disponible === false
    },
    afficherBlocMeteoCarte(marche) {
      return this.hasMeteoUtile(marche)
    },
    impactMeteo(marche) {
      const meteo = this.contexteMeteo(marche)
      return String(meteo.impact_meteo || meteo.impact_indicatif || 'inconnu').toLowerCase()
    },
    conseilMeteo(marche) {
      if (!this.hasMeteoUtile(marche)) return ''

      const impact = this.impactMeteo(marche)
      if (impact === 'risque') return 'Adapter la préparation selon la météo.'
      if (impact === 'a_surveiller') return 'Surveiller la météo avant le marché.'
      return ''
    },
    resumeMeteo(marche) {
      return String(this.contexteMeteo(marche).resume || '').trim()
    },
    resumeMeteoExploitable(marche) {
      const resume = this.resumeMeteo(marche)
      if (!resume) return false

      const min = resume.toLowerCase()
      return !min.includes('météo indisponible') && !min.includes('meteo indisponible')
    },
    titreMeteo(marche) {
      if (this.hasMeteoUtile(marche)) return 'Prévision disponible'
      return ''
    },
    aDonneeMeteoUtile(marche) {
      return this.donneesMeteoUtiles(marche).length > 0
    },
    hasMeteoUtile(marche) {
      return this.aContexteMeteo(marche) &&
        this.meteoDisponible(marche) &&
        (this.aDonneeMeteoUtile(marche) || this.resumeMeteoExploitable(marche))
    },
    afficherResumeMeteo(marche) {
      return this.hasMeteoUtile(marche) && this.resumeMeteoExploitable(marche)
    },
    afficherBadgeImpactMeteo(marche) {
      return this.hasMeteoUtile(marche) && ['favorable', 'a_surveiller', 'risque'].includes(this.impactMeteo(marche))
    },
    valeurMeteoNumerique(value) {
      return typeof value === 'number' && Number.isFinite(value)
    },
    nombreMeteo(value, maximumFractionDigits = 1) {
      if (!this.valeurMeteoNumerique(value)) return null

      return new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits
      }).format(value)
    },
    premiereValeurMeteoNumerique(meteo, keys) {
      return keys.map(key => meteo[key]).find(this.valeurMeteoNumerique)
    },
    temperatureMeteo(marche) {
      const meteo = this.contexteMeteo(marche)
      const min = this.nombreMeteo(meteo.temperature_min)
      const max = this.nombreMeteo(meteo.temperature_max)

      if (min !== null && max !== null) return `${min} à ${max} °C`
      if (min !== null) return `Min. ${min} °C`
      if (max !== null) return `Max. ${max} °C`
      return ''
    },
    pluieMeteo(marche) {
      const precipitation = this.nombreMeteo(this.contexteMeteo(marche).precipitation_mm)
      return precipitation !== null ? `Pluie ${precipitation} mm` : ''
    },
    probabilitePluieMeteo(marche) {
      const meteo = this.contexteMeteo(marche)
      const value = this.premiereValeurMeteoNumerique(meteo, ['probabilite_pluie', 'probabilite_precipitation'])
      const probabilite = this.nombreMeteo(value, 0)
      return probabilite !== null ? `Probabilité pluie ${probabilite} %` : ''
    },
    ventMeteo(marche) {
      const meteo = this.contexteMeteo(marche)
      const value = this.premiereValeurMeteoNumerique(meteo, ['vent_kmh', 'vent_max_kmh'])
      const vent = this.nombreMeteo(value, 0)
      return vent !== null ? `Vent max ${vent} km/h` : ''
    },
    donneesMeteoUtiles(marche) {
      return [
        this.temperatureMeteo(marche),
        this.pluieMeteo(marche),
        this.probabilitePluieMeteo(marche),
        this.ventMeteo(marche)
      ].filter(Boolean)
    },
    impactMeteoClass(marche) {
      const impact = this.impactMeteo(marche)
      if (impact === 'a_surveiller') return 'a-surveiller'
      if (impact === 'favorable') return 'favorable'
      if (impact === 'risque') return 'risque'
      return 'neutre'
    },
    libelleImpactMeteo(marche) {
      const impact = this.impactMeteo(marche)
      if (impact === 'favorable') return 'Météo favorable'
      if (impact === 'a_surveiller') return 'Météo à surveiller'
      if (impact === 'risque') return 'Risque météo'
      return ''
    },
    potentielClass(potentiel) {
      const value = String(potentiel || '').toLowerCase()
      if (value.includes('très') || value.includes('tres')) return 'tres-bon'
      if (value.includes('bon')) return 'bon'
      if (value.includes('moyen')) return 'moyen'
      if (value.includes('faible')) return 'faible'
      return 'inconnu'
    },
    confianceClass(confiance) {
      const value = String(confiance || '').toLowerCase()
      if (value.includes('bonne')) return 'bonne'
      if (value.includes('moyenne')) return 'moyenne'
      return 'faible'
    },
    conseilMarchand(marche) {
      const conseilsParPotentiel = {
        faible: 'Prévoir stock réduit',
        moyen: 'Prévoir stock habituel',
        bon: 'Prévoir stock renforcé',
        'tres-bon': "Anticiper plus d'affluence",
        inconnu: 'Données insuffisantes'
      }
      const potentiel = this.potentielClass(marche.potentiel)
      const confiance = this.confianceClass(marche.confiance)
      let precision = ''

      if (confiance === 'faible') {
        precision = 'À confirmer avec votre expérience terrain'
      } else if (confiance === 'bonne') {
        precision = "Prévision plus fiable grâce à l'historique disponible"
      }

      return {
        action: conseilsParPotentiel[potentiel] || conseilsParPotentiel.inconnu,
        precision
      }
    },
    phraseLecture(marche) {
      if (!this.aFourchette(marche)) return 'Historique insuffisant'
      const potentiel = String(marche.potentiel || '').toLowerCase()
      if (potentiel.includes('bon')) return 'Marché à préparer avec attention'
      if (potentiel.includes('moyen')) return 'Marché dans la tendance habituelle'
      if (potentiel.includes('faible')) return 'Prévoir prudemment'
      return 'Tendance à confirmer'
    },
    facteursVisibles(marche) {
      const facteurs = Array.isArray(marche.facteurs_explicatifs) ? marche.facteurs_explicatifs : []
      const visibles = facteurs
        .filter(facteur => !this.estFacteurHistoriqueDejaAffiche(facteur, marche))
        .filter(facteur => !this.estFacteurMeteoDejaAffiche(facteur, marche))
        .map(this.simplifierFacteur)
      const dedup = [...new Set(visibles)]

      return dedup
    },
    estFacteurHistoriqueDejaAffiche(facteur, marche) {
      if (!this.aHistorique(marche)) return false

      const min = String(facteur || '').toLowerCase()
      return min.startsWith('ca moyen observé sur les journées comparables') ||
        min.startsWith('médiane utilisée comme base prudente')
    },
    estFacteurMeteoDejaAffiche(facteur, marche) {
      const min = String(facteur || '').toLowerCase()
      return min.includes('météo') || min.includes('meteo')
    },
    simplifierFacteur(facteur) {
      const texte = String(facteur || '')
      const min = texte.toLowerCase()

      if (min.includes('aucune journée historique')) {
        return "Aucune journée passée avec chiffre d'affaires n'est encore disponible."
      }
      if (min.includes('historique local') || min.includes('historique global commerçant')) {
        return "Pas assez d'historique précis pour cette ville ou ce jour : l'estimation s'appuie sur l'historique global."
      }
      if (min.includes('prévision basée sur')) {
        return texte
      }
      if (min.includes('médiane utilisée')) {
        return "Base calculée à partir des chiffres d'affaires observés sur les journées comparables."
      }
      if (min.includes('potentiel évalué')) {
        return 'Potentiel comparé aux marchés habituels du commerçant.'
      }
      if (min.includes('médiane globale insuffisamment fiable')) {
        return 'Historique global encore limité : potentiel à confirmer.'
      }
      if (min.includes('fourchette bornée')) {
        return 'Journées atypiques limitées pour éviter une estimation trop haute ou trop basse.'
      }
      if (min.includes('fourchette prudente')) {
        return 'Fourchette volontairement prudente pour tenir compte des écarts entre marchés.'
      }
      if (min.includes('météo') || min.includes('meteo')) {
        return texte
      }

      return texte
    }
  }
}
</script>

<style scoped>
.previsions-page {
  max-width: 1180px;
}

.previsions-header {
  margin-bottom: 18px;
}

.eyebrow {
  color: var(--fm-accent);
  text-transform: uppercase;
  letter-spacing: 0;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-title {
  font-size: 32px;
  margin-bottom: 8px;
}

.page-subtitle {
  max-width: 720px;
  color: var(--fm-muted);
}

.notice-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: var(--fm-card-radius);
  padding: 16px 18px;
  margin-bottom: 18px;
  box-shadow: var(--fm-shadow-subtle);
}

.notice-card strong {
  color: #92400E;
  white-space: nowrap;
}

.notice-card span {
  color: #78350F;
  font-weight: 600;
}

.period-filter {
  display: grid;
  gap: 12px;
  background: var(--fm-surface);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-card-radius);
  padding: 14px;
  margin-bottom: 18px;
  box-shadow: var(--fm-shadow-subtle);
}

.period-filter-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.filter-label {
  display: block;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.period-filter strong {
  display: block;
  color: var(--fm-text);
  font-size: 16px;
  line-height: 1.25;
}

.period-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.period-btn {
  width: 100%;
  min-height: 40px;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  background: var(--fm-surface-soft);
  color: var(--fm-text);
  font-weight: 800;
  cursor: pointer;
}

.period-btn.active {
  background: var(--fm-navy);
  border-color: var(--fm-navy);
  color: #FFFFFF;
}

.attention-summary {
  display: grid;
  gap: 10px;
  background: var(--fm-surface);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-card-radius);
  padding: 14px;
  margin-bottom: 18px;
  box-shadow: var(--fm-shadow-subtle);
}

.attention-summary-top h2 {
  color: var(--fm-text);
  font-size: 17px;
  line-height: 1.25;
}

.attention-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.attention-list li {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 30px;
  max-width: 100%;
  background: var(--fm-surface-soft);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 750;
  line-height: 1.25;
  padding: 6px 9px;
}

.attention-list strong {
  color: var(--fm-text);
  font-size: 14px;
}

.attention-list span {
  overflow-wrap: anywhere;
}

.attention-summary p {
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 650;
}

.custom-period {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.custom-period label {
  display: grid;
  gap: 5px;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 800;
}

.custom-period input {
  min-height: 42px;
  width: 100%;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  background: var(--fm-surface);
  color: var(--fm-text);
  font: inherit;
  font-weight: 700;
  padding: 8px 10px;
}

.state-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 28px;
}

.state-card strong {
  font-size: 18px;
}

.state-card span {
  color: var(--fm-muted);
}

.error-state {
  border-color: #FECACA;
  background: #FEF2F2;
}

.retry-btn,
.state-link {
  width: auto;
  margin-top: 8px;
  text-decoration: none;
}

.loading-pulse {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: var(--fm-accent-soft);
  position: relative;
}

.loading-pulse::after {
  content: '';
  position: absolute;
  inset: 9px;
  border-radius: inherit;
  background: var(--fm-accent);
  animation: pulse 1s ease-in-out infinite;
}

.previsions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
  gap: 16px;
}

.prevision-card {
  background: var(--fm-surface);
  border: 1px solid var(--fm-border);
  border-left: 6px solid var(--fm-border);
  border-radius: var(--fm-card-radius);
  padding: 18px;
  box-shadow: var(--fm-shadow-subtle);
}

.prevision-card.potentiel-tres-bon,
.prevision-card.potentiel-bon {
  border-left-color: var(--fm-success);
}

.prevision-card.potentiel-moyen {
  border-left-color: var(--fm-warning);
}

.prevision-card.potentiel-faible {
  border-left-color: var(--fm-danger);
}

.prevision-card.potentiel-inconnu {
  border-left-color: #94A3B8;
}

.card-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.date-line,
.hours-line {
  color: var(--fm-muted);
  font-weight: 650;
}

.date-line {
  text-transform: capitalize;
  font-size: 13px;
  margin-bottom: 4px;
}

.card-top h2 {
  font-size: 24px;
  margin-bottom: 4px;
  overflow-wrap: anywhere;
}

.hours-line {
  font-size: 15px;
}

.badge-indicative {
  background: var(--fm-surface-soft);
  color: var(--fm-muted);
  border-color: var(--fm-border);
  white-space: nowrap;
}

.badges-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.potentiel-badge,
.confiance-badge {
  text-transform: capitalize;
}

.badge-tres-bon,
.badge-bon,
.confiance-bonne {
  background: var(--fm-success-soft);
  color: #166534;
  border-color: #BBF7D0;
}

.badge-moyen,
.confiance-moyenne {
  background: var(--fm-warning-soft);
  color: #92400E;
  border-color: #FDE68A;
}

.badge-faible,
.confiance-faible {
  background: var(--fm-danger-soft);
  color: #991B1B;
  border-color: #FECACA;
}

.badge-inconnu {
  background: var(--fm-surface-soft);
  color: var(--fm-muted);
  border-color: var(--fm-border);
}

.vacances-badge {
  background: var(--fm-accent-soft);
  color: #1D4ED8;
  border-color: #BFDBFE;
}

.vacances-unavailable {
  align-self: center;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
}

.weather-block {
  display: grid;
  gap: 8px;
  background: #F8FAFC;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  padding: 12px;
  margin-bottom: 14px;
}

.weather-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.weather-main {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.weather-main span {
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.weather-main strong {
  color: var(--fm-text);
  font-size: 14px;
  line-height: 1.25;
}

.weather-summary {
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 650;
  line-height: 1.3;
}

.weather-details {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.weather-details span {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  max-width: 100%;
  background: var(--fm-surface);
  border: 1px solid var(--fm-border);
  border-radius: 999px;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 750;
  line-height: 1.2;
  padding: 4px 8px;
  overflow-wrap: anywhere;
}

.weather-impact {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  max-width: 100%;
  border: 1px solid var(--fm-border);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.2;
  padding: 4px 8px;
  text-align: right;
  white-space: normal;
}

.impact-a-surveiller {
  background: var(--fm-warning-soft) !important;
  color: #92400E !important;
  border-color: #FDE68A !important;
}

.impact-favorable {
  background: var(--fm-success-soft) !important;
  color: #166534 !important;
  border-color: #BBF7D0 !important;
}

.impact-risque {
  background: var(--fm-danger-soft) !important;
  color: #991B1B !important;
  border-color: #FECACA !important;
}

.impact-neutre {
  background: var(--fm-surface-soft) !important;
  color: var(--fm-muted) !important;
  border-color: var(--fm-border) !important;
}

.ca-block {
  background: #F8FAFC;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  padding: 16px;
  margin-bottom: 14px;
}

.merchant-advice {
  display: grid;
  gap: 4px;
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: var(--fm-control-radius);
  padding: 14px;
  margin-bottom: 14px;
}

.merchant-advice span {
  color: #166534;
  font-size: 13px;
  font-weight: 800;
}

.merchant-advice strong {
  color: var(--fm-text);
  font-size: 18px;
  line-height: 1.25;
}

.merchant-advice small {
  color: #166534;
  font-size: 13px;
  font-weight: 650;
  line-height: 1.3;
}

.ca-block.muted {
  background: var(--fm-surface-soft);
}

.ca-label {
  display: block;
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 4px;
}

.ca-block strong {
  display: block;
  color: var(--fm-navy);
  font-size: 25px;
  line-height: 1.2;
}

.trend-block {
  display: grid;
  gap: 6px;
  background: var(--fm-surface-soft);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  padding: 12px;
  margin: -4px 0 14px;
}

.trend-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.trend-main span {
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 800;
}

.trend-badge {
  text-align: right;
  line-height: 1.2;
}

.trend-block small,
.trend-values {
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 650;
  line-height: 1.3;
}

.trend-values {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}

.trend-baisse {
  background: var(--fm-danger-soft);
  color: #991B1B;
  border-color: #FECACA;
}

.trend-stable,
.trend-insuffisant {
  background: var(--fm-surface);
  color: var(--fm-muted);
  border-color: var(--fm-border);
}

.trend-hausse {
  background: var(--fm-success-soft);
  color: #166534;
  border-color: #BBF7D0;
}

.trend-forte-hausse {
  background: #DCFCE7;
  color: #14532D;
  border-color: #86EFAC;
}

.history-metrics {
  display: grid;
  gap: 8px;
  margin: -4px 0 14px;
}

.history-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: var(--fm-surface-soft);
  border-radius: var(--fm-control-radius);
}

.history-row span {
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 700;
}

.history-row strong {
  color: var(--fm-text);
  font-size: 14px;
  white-space: nowrap;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.visible-details {
  grid-template-columns: 1fr;
  margin-bottom: 10px;
}

.detail-item {
  background: var(--fm-surface-soft);
  border-radius: var(--fm-control-radius);
  padding: 12px;
  min-width: 0;
}

.detail-item span {
  display: block;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 4px;
}

.detail-item strong {
  color: var(--fm-text);
  font-size: 14px;
  line-height: 1.25;
}

.details-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 46px;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  background: var(--fm-surface);
  color: var(--fm-text);
  font: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  touch-action: manipulation;
}

.details-toggle:hover {
  background: var(--fm-surface-soft);
}

.toggle-arrow {
  color: var(--fm-muted);
  font-size: 17px;
  line-height: 1;
}

.details-panel {
  display: grid;
  gap: 12px;
  margin-top: 12px;
  overflow: hidden;
}

.long-detail strong {
  display: block;
}

.details-enter-active,
.details-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.details-enter-from,
.details-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.factors {
  border-top: 1px solid var(--fm-border);
  padding-top: 14px;
}

.factors h3 {
  font-size: 15px;
  margin-bottom: 8px;
}

.factors ul {
  display: grid;
  gap: 7px;
  padding-left: 18px;
}

.factors li {
  color: var(--fm-muted);
  font-size: 13px;
}

.mini-list {
  display: grid;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
}

.mini-market {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: var(--fm-surface-soft);
  border-radius: var(--fm-control-radius);
}

.mini-market-main {
  display: grid;
  gap: 4px;
}

.mini-market-main span {
  color: var(--fm-text);
  font-weight: 700;
}

.mini-market-main small {
  color: var(--fm-muted);
  text-align: right;
}

@keyframes pulse {
  0%, 100% { transform: scale(0.8); opacity: 0.6; }
  50% { transform: scale(1); opacity: 1; }
}

@media (max-width: 700px) {
  .previsions-page {
    padding: 18px 14px;
  }

  .page-title {
    font-size: 28px;
  }

  .notice-card,
  .card-top,
  .mini-market {
    flex-direction: column;
  }

  .notice-card strong,
  .badge-indicative {
    white-space: normal;
  }

  .period-filter {
    padding: 12px;
  }

  .previsions-grid {
    grid-template-columns: 1fr;
  }

  .prevision-card {
    padding: 16px;
  }

  .ca-block strong {
    font-size: 22px;
  }

  .trend-main {
    flex-direction: column;
  }

  .trend-badge {
    text-align: left;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .mini-market .vacances-unavailable {
    align-self: flex-start;
  }

  .mini-market-main small {
    text-align: left;
  }
}

@media (min-width: 720px) {
  .period-filter {
    grid-template-columns: minmax(180px, 1fr) minmax(360px, 2fr);
    align-items: start;
  }

  .period-actions {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .custom-period {
    grid-column: 2;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
